import { useMemo } from 'react';

import { INSURANCE_CARRIER_CONTACTS } from '@/constants/insurance-carriers';
import { useGetCoveragesQuery } from '@/services/api/coverageService';
import { useGetNotesQuery } from '@/services/api/matterNotesService';
import {
  ContactHandleSchema,
  CoverageCategory,
} from '@/services/types/coverage-types';
import { InsuranceNote, NOTE_TYPES } from '@/services/types/matter-notes-types';
import { isPhoneNumberValid } from '@/utils/validation';

export interface InsuranceContact {
  id: string;
  name: string;
  secondaryTitle?: string;
  phone: string;
  type: 'carrier' | 'adjuster' | 'subrogator' | 'directory';
  coverageId?: string;
  coverageCategory?: CoverageCategory | null;
}

/**
 * Format coverage category for display (1P or 3P).
 */
export const formatCoverageCategory = (
  category: CoverageCategory | null | undefined,
): string => {
  if (!category) return '';
  switch (category) {
    case CoverageCategory.FIRST_PARTY:
    case CoverageCategory.FIRST_PARTY_HEALTH:
      return '1P';
    case CoverageCategory.THIRD_PARTY:
    case CoverageCategory.PROPERTY:
      return '3P';
    default:
      return '';
  }
};

interface UseInsuranceContactsResult {
  contacts: InsuranceContact[];
  isLoading: boolean;
  carrierContacts: InsuranceContact[];
  adjusterContacts: InsuranceContact[];
  directoryContacts: InsuranceContact[];
}

/**
 * Hook to fetch insurance-related contacts for a matter.
 * Aggregates contacts from:
 * - Coverage carriers (phone handles)
 * - Coverage adjusters (phone handles)
 * - Coverage subrogators (phone handles)
 * - Insurance notes with adjuster phones
 * - Hardcoded insurance carrier directory
 */
export const useInsuranceContacts = (
  matterId: string,
): UseInsuranceContactsResult => {
  const { data: coverages, isLoading: coveragesLoading } = useGetCoveragesQuery(
    { matterId },
    { skip: !matterId },
  );

  const { data: insuranceNotes, isLoading: notesLoading } = useGetNotesQuery(
    { matterId, noteType: NOTE_TYPES.INSURANCE },
    { skip: !matterId },
  );

  const contacts = useMemo(() => {
    const allContacts: InsuranceContact[] = [];

    // Coverage contacts (carriers, adjusters, subrogators)
    if (coverages) {
      coverages.forEach((coverage) => {
        // Carrier contacts
        if (coverage.carrier?.handles) {
          const phoneHandles = coverage.carrier.handles.filter(
            (handle: ContactHandleSchema) =>
              handle.handle_type === 'PHONE' &&
              handle.value &&
              isPhoneNumberValid(handle.value),
          );

          phoneHandles.forEach((handle, index) => {
            allContacts.push({
              id: `coverage-${coverage.id}-carrier-${index}`,
              name: coverage.carrier?.name || 'Unknown Carrier',
              secondaryTitle: handle.name || 'Carrier',
              phone: handle.value,
              type: 'carrier',
              coverageId: coverage.id,
              coverageCategory: coverage.category,
            });
          });
        }

        // Adjuster contacts
        if (coverage.adjusters?.[0]?.handles) {
          const phoneHandles = coverage.adjusters[0].handles.filter(
            (handle: ContactHandleSchema) =>
              handle.handle_type === 'PHONE' &&
              handle.value &&
              isPhoneNumberValid(handle.value),
          );

          phoneHandles.forEach((handle: ContactHandleSchema, index: number) => {
            allContacts.push({
              id: `coverage-adjuster-${coverage.id}-${index}`,
              name: coverage.adjusters?.[0]?.name || 'Unknown Adjuster',
              secondaryTitle: handle.name || 'Adjuster',
              phone: handle.value,
              type: 'adjuster',
              coverageId: coverage.id,
              coverageCategory: coverage.category,
            });
          });
        }

        // Subrogator contacts
        if (coverage.subrogator?.handles) {
          const phoneHandles = coverage.subrogator.handles.filter(
            (handle: ContactHandleSchema) =>
              handle.handle_type === 'PHONE' &&
              handle.value &&
              isPhoneNumberValid(handle.value),
          );

          phoneHandles.forEach((handle, index) => {
            allContacts.push({
              id: `coverage-${coverage.id}-subrogator-${index}`,
              name: coverage.subrogator?.name || 'Unknown Subrogator',
              secondaryTitle: handle.name || 'Subrogator',
              phone: handle.value,
              type: 'subrogator',
              coverageId: coverage.id,
              coverageCategory: coverage.category,
            });
          });
        }
      });
    }

    // Insurance note contacts
    if (insuranceNotes) {
      (insuranceNotes as InsuranceNote[])
        .filter((note) => note.adjuster_phone && isPhoneNumberValid(note.adjuster_phone))
        .forEach((note) => {
          allContacts.push({
            id: `note-${note.id}`,
            name: note.carrier || 'Unknown Carrier',
            secondaryTitle: note.adjuster_name || 'Adjuster',
            phone: note.adjuster_phone || '',
            type: 'adjuster',
          });
        });
    }

    return allContacts;
  }, [coverages, insuranceNotes]);

  // Categorized contacts
  const carrierContacts = useMemo(
    () => contacts.filter((c) => c.type === 'carrier'),
    [contacts],
  );

  const adjusterContacts = useMemo(
    () => contacts.filter((c) => c.type === 'adjuster' || c.type === 'subrogator'),
    [contacts],
  );

  const directoryContacts = useMemo(() => {
    return INSURANCE_CARRIER_CONTACTS.filter(
      (carrier) => carrier.phone && isPhoneNumberValid(carrier.phone),
    ).map((carrier) => ({
      id: `directory-${carrier.name}`,
      name: carrier.name,
      phone: carrier.phone || '',
      type: 'directory' as const,
    }));
  }, []);

  return {
    contacts,
    isLoading: coveragesLoading || notesLoading,
    carrierContacts,
    adjusterContacts,
    directoryContacts,
  };
};
